// CoordinateCircle.js
import { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";

function approximateCircleLines(r = 4, step = 0.2, color = "gray") {
  const segments = [];
  for (let x = -r; x < r; x += step) {
    const nextX = x + step > r ? r : x + step;
    const y1 = Math.sqrt(r * r - x * x);
    const y2 = Math.sqrt(r * r - nextX * nextX);

    // upper arc
    segments.push({
      start: [x, y1],
      end: [nextX, y2],
      color
    });
    // lower arc
    segments.push({
      start: [x, -y1],
      end: [nextX, -y2],
      color
    });
  }
  return segments;
}

/**
 * A specialized coordinate-plane that:
 * - draws a circle of radius `circleRadius` centered at (0,0) by approximating arcs
 * - optionally draws lines, grid, axes
 * - can also plot points on click if disablePlot is false
 */
export default function CoordinateCircle({
  width = 400,
  height = 400,
  showGrid = false,
  showAxes = false,
  lines = [],
  circleRadius = 4,        // radius of the circle
  circleColor = "gray",    // color for circle arcs
  disablePlot = false
}) {
  const svgRef = useRef(null);
  const [points, setPoints] = useState([]);

  // Basic domain for -10..+10 or expand if your circle is bigger
  const xDomain = useMemo(() => [-10, 10], []);
  const yDomain = useMemo(() => [-10, 10], []);

  // Scales
  const xScale = d3.scaleLinear().domain(xDomain).range([0, width]);
  const yScale = d3.scaleLinear().domain(yDomain).range([height, 0]);

  // Precompute line segments that approximate the circle
  const circleSegments = approximateCircleLines(circleRadius, 0.2, circleColor);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Optionally draw grid
    if (showGrid) {
      // vertical grid lines
      svg.append("g")
        .selectAll("line.xgrid")
        .data(d3.range(xDomain[0], xDomain[1] + 1))
        .enter()
        .append("line")
        .attr("class", "xgrid")
        .attr("x1", d => xScale(d))
        .attr("y1", 0)
        .attr("x2", d => xScale(d))
        .attr("y2", height)
        .attr("stroke", "#eee");

      // horizontal grid lines
      svg.append("g")
        .selectAll("line.ygrid")
        .data(d3.range(yDomain[0], yDomain[1] + 1))
        .enter()
        .append("line")
        .attr("class", "ygrid")
        .attr("x1", 0)
        .attr("y1", d => yScale(d))
        .attr("x2", width)
        .attr("y2", d => yScale(d))
        .attr("stroke", "#eee");
    }

    // Optionally draw axes
    if (showAxes) {
      const xAxis = d3.axisBottom(xScale)
        .tickValues(d3.range(xDomain[0], xDomain[1] + 1, 2));
      svg.append("g")
        .attr("transform", `translate(0, ${yScale(0)})`)
        .call(xAxis);

      const yAxis = d3.axisLeft(yScale)
        .tickValues(d3.range(yDomain[0], yDomain[1] + 1, 2));
      svg.append("g")
        .attr("transform", `translate(${xScale(0)}, 0)`)
        .call(yAxis);
    }

    // Draw the circle arcs
    circleSegments.forEach(seg => {
      svg.append("line")
        .attr("x1", xScale(seg.start[0]))
        .attr("y1", yScale(seg.start[1]))
        .attr("x2", xScale(seg.end[0]))
        .attr("y2", yScale(seg.end[1]))
        .attr("stroke", seg.color)
        .attr("stroke-width", 2);
    });

    // Draw any additional lines passed in
    lines.forEach(({ start, end, color = "black", label }) => {
      svg.append("line")
        .attr("x1", xScale(start[0]))
        .attr("y1", yScale(start[1]))
        .attr("x2", xScale(end[0]))
        .attr("y2", yScale(end[1]))
        .attr("stroke", color)
        .attr("stroke-width", 2);

      if (label) {
        const mx = (start[0] + end[0]) / 2;
        const my = (start[1] + end[1]) / 2;
        svg.append("text")
          .attr("x", xScale(mx))
          .attr("y", yScale(my) - 5)
          .attr("fill", color)
          .attr("font-size", 12)
          .text(label);
      }
    });

    // Plot points if not disabled
    if (!disablePlot) {
      // existing points
      svg.selectAll(".point")
        .data(points)
        .enter()
        .append("circle")
        .attr("class", "point")
        .attr("cx", d => xScale(d.x))
        .attr("cy", d => yScale(d.y))
        .attr("r", 4)
        .attr("fill", "red");

      // clickable rect to add new points
      svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "transparent")
        .on("click", event => {
          const [px, py] = d3.pointer(event);
          const xVal = xScale.invert(px);
          const yVal = yScale.invert(py);
          // round to 1 decimal
          setPoints(curr => [...curr, {
            x: +xVal.toFixed(1),
            y: +yVal.toFixed(1)
          }]);
        });
    }
    }, [
    points, showGrid, showAxes, lines, circleSegments, disablePlot,
    height, width, xDomain, yDomain, xScale, yScale
  ]);

  return (
    <div style={{ border: "1px solid #ccc", display: "inline-block" }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: "block" }}
      />
      {/* List points if plotting is enabled */}
      {!disablePlot && (
        <div style={{ padding: 5 }}>
          <strong>Plotted Points:</strong>
          <ul style={{ marginTop: 5 }}>
            {points.map((pt, i) => (
              <li key={i}>
                ( {pt.x}, {pt.y} )
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
